import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { Navbar } from '../../components/Navbar';
import Footer from '../../components/Footer';
import { LoadingSpinner } from '../../components/common';
import { api } from '../../services/authenticatedApi';

interface KnowledgeUpdate {
  id: string;
  entitySlug: string;
  entityName: string;
  outcome: string;
  confidenceBefore: number;
  confidenceAfter: number;
  summary: string;
  occurredAt: string;
}

export const AdminKnowledgeUpdatesPage: React.FC = () => {
  const [updates, setUpdates] = useState<KnowledgeUpdate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ updates: KnowledgeUpdate[] }>('/admin/knowledge-updates')
      .then((res) => setUpdates(res.data.updates))
      .catch(() => setUpdates([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar pageTitle="Knowledge updates" showDivider={true} />
      <main className="pt-(--navbar-offset) max-w-3xl mx-auto px-4 pb-16">
        <div className="mt-8 mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Knowledge updates
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            How task outcomes are reshaping Autar's derived knowledge — the flywheel learning.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center pt-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : updates.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">No knowledge updates yet.</p>
        ) : (
          <ul className="space-y-3">
            {updates.map((u) => {
              const up = u.confidenceAfter >= u.confidenceBefore;
              return (
                <li
                  key={u.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  {up ? (
                    <TrendingUp className="w-5 h-5 text-green-500 shrink-0" />
                  ) : (
                    <TrendingDown className="w-5 h-5 text-red-400 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900 dark:text-gray-200">{u.summary}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(u.occurredAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-sm font-mono shrink-0">
                    <span className="text-gray-400">{u.confidenceBefore.toFixed(2)}</span>
                    <span className="mx-1 text-gray-300">→</span>
                    <span className={up ? 'text-green-600 dark:text-green-400' : 'text-red-500'}>
                      {u.confidenceAfter.toFixed(2)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>
      <Footer />
    </div>
  );
};
